import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MyCalendar.css';
import EventDetailsModal from './EventDetailsModal'; 
import { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import TimezoneSelector from './TimezoneSelector'; // Import the selector
import { jwtDecode } from 'jwt-decode';
import UpgradePrompt from './UpgradePrompt';

const localizer = momentLocalizer(moment);

function MyCalendar() {
  const [events, setEvents] = useState([]);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clients, setClients] = useState([]);
  const { userId } = useParams(); // Extract the userId from the URL
  const [userData, setUserData] = useState(null); // New state to store user data
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'success' or 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [isPro, setIsPro] = useState(null);
  const [publicLinkCopied, setPublicLinkCopied] = useState(false);
  const [circleEvents, setCircleEvents] = useState([]);
  const [circleId, setCircleId] = useState(null);
  const [logoutConfirmed, setLogoutConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [userTimezone, setUserTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' // Default to detected timezone or UTC
  );  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: '',
    startTime: '',
    endTime: '',
    clientId: '', // New field to store the selected client's ID
    clientName: '', // Add clientName here
    clientEmail: '', // Ensure clientEmail is included
    color: '#1B2B4B', // Navy default
    hostLink: '',
    guestLink: '',
    addVideoConference: false, // Add this field
  });

  const navigate = useNavigate();

  // Check Pro status
  useEffect(() => {
    const checkPlan = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsPro(['pro','circle_member','circle_3','circle_5','circle_10'].includes(response.data.plan?.toLowerCase()));
      } catch (error) {
        console.error('Error checking plan:', error);
        setIsPro(false);
      }
    };
    checkPlan();
  }, []);

// Function to open modal with specific type and message
const showModal = (type, message) => {
  setModalType(type);
  setModalMessage(message);
  setModalVisible(true);
};

// Function to close modal
const closeModal = () => {
  setModalVisible(false);
};

const handleTimezoneChange = (timezone) => {
  setUserTimezone(timezone);
};

// Function to handle logout using modal
const handleLogout = useCallback(() => {
  showModal('confirm', 'Are you sure you want to log out?');
}, []);

// Handle confirming the logout action
const confirmLogout = () => {
  localStorage.removeItem('token');
  navigate('/login');
};

// Function to handle deleting an event using modal for confirmation
const handleDeleteEvent = () => {
  showModal('confirm', 'Are you sure you want to delete this event?');
};

// Function to handle the actual deletion of the event once confirmed
const handleDeleteEventConfirmed = async () => {
  const token = localStorage.getItem('token');
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/events/${selectedEvent.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setEvents((prevEvents) => prevEvents.filter((event) => event.id !== selectedEvent.id));
    setShowEventDetailsModal(false);
  } catch (error) {
    console.error('Failed to delete event:', error);
    setModalMessage('Failed to delete event. Please try again.');
    setModalType('error');
    setModalVisible(true);
  }
};

// Function to confirm the action based on modalType
const handleConfirm = () => {
  if (modalType === 'confirm') {
    if (modalMessage === 'Are you sure you want to log out?') {
      confirmLogout();
    } else if (modalMessage === 'Are you sure you want to delete this event?') {
      handleDeleteEventConfirmed();
    }
  }
  closeModal();
};


  // Function to check if the token has expired
  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('token');
      if (token) {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
  
        // Check if the token is expired
        if (decodedToken.exp < currentTime) {
          handleLogout(); // Log out if the token has expired
        }
      } else {
        handleLogout(); // If no token, force logout
      }
    };
  
    checkTokenExpiration(); // Run the token check immediately
  
    const intervalId = setInterval(() => {
      checkTokenExpiration(); // Check token every 60 seconds
    }, 60000);
  
    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [handleLogout]); // Add handleLogout to the dependency array


  useEffect(() => {
    const fetchLocalEvents = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const formattedLocalEvents = response.data.map(event => ({
          id: event._id,
          title: event.title,
          start: moment.tz(event.startTime, 'UTC').tz(moment.tz.guess()).toDate(),
          end: moment.tz(event.endTime, 'UTC').tz(moment.tz.guess()).toDate(),
          color: event.color || '#1B2B4B',
          description: event.description,
          location: event.location,
          hostLink: event.hostLink,
          guestLink: event.guestLink,
          clientName: event.clientName || 'Unknown', // Store the client name
        }));
        setEvents(prevEvents => [...prevEvents, ...formattedLocalEvents]);
      } catch (error) {
        console.error('Failed to fetch local events:', error);
      }
    };
    fetchLocalEvents();
  }, []);

  // Fetch Circle events — shows teammates' shared events in coral
  useEffect(() => {
    const fetchCircleEvents = async () => {
      try {
        const token = localStorage.getItem('token');
        // First get circle membership
        const circleRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/circles/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cId = circleRes.data.circle?._id;
        if (!cId) return;
        setCircleId(cId);

        const eventsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/circles/${cId}/events`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const userId = localStorage.getItem('userId');
        const formatted = (eventsRes.data.events || [])
          .filter(evt => evt.createdBy?._id !== userId && evt.createdBy !== userId) // exclude own events (already shown)
          .map(evt => ({
            id: `circle_${evt._id}`,
            title: `✦ ${evt.title}${evt.creatorName ? ` (${evt.creatorName.split(' ')[0]})` : ''}`,
            start: new Date(evt.startTime),
            end: new Date(evt.endTime),
            color: '#E8654A', // coral for Circle events
            description: evt.description,
            location: evt.location,
            isCircleEvent: true,
            creatorName: evt.creatorName,
            clientName: evt.clientName,
          }));

        setCircleEvents(formatted);
        setEvents(prev => {
          // Remove any stale circle events then add fresh ones
          const personal = prev.filter(e => !e.isCircleEvent);
          return [...personal, ...formatted];
        });
      } catch (err) {
        // No circle or not a member — silent fail
      }
    };
    fetchCircleEvents();
  }, []);


  useEffect(() => {
    const fetchGoogleEvents = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/google/sync-google-calendar`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const formattedGoogleEvents = response.data.map(event => ({
          title: event.title,
          start: moment.tz(event.startTime, 'UTC').tz(moment.tz.guess()).toDate(),
          end: moment.tz(event.endTime, 'UTC').tz(moment.tz.guess()).toDate(),
          description: event.description,
          location: event.location,
          color: '#E8A030' // Warm amber for Google Calendar events,
        }));
        setEvents(prevEvents => [...prevEvents, ...formattedGoogleEvents]);
      } catch (error) {
        console.error('Failed to fetch Google events:', error);
      }
    };
    fetchGoogleEvents();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        setUserData(response.data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setClients(response.data);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      }
    };
    fetchClients();
  }, []);

  // Fetch user information to display the user's name
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`, // Send token in the header
          },
        });
        setUserData(response.data.user); // Store user data
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);
  
  const fetchGoogleEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/google/sync-google-calendar`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched Google Events:', response.data);
  
      const formattedGoogleEvents = response.data.map(event => ({
        title: event.title,
        start: new Date(event.startTime),
        end: new Date(event.endTime),
        description: event.description,
        location: event.location,
        color: '#E8A030' // Warm amber for Google Calendar events,
      }));
      setEvents(formattedGoogleEvents);
      localStorage.setItem('googleEvents', JSON.stringify(formattedGoogleEvents));
      console.log('Events saved to localStorage');
    } catch (error) {
      console.error('Failed to fetch Google events:', error);
    }
  };

  const handleSelectEvent = (event) => {
    setFormData({
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      date: moment.tz(event.start, userTimezone).format('YYYY-MM-DD'),
      startTime: moment.tz(event.start, userTimezone).format('HH:mm'),
      endTime: moment.tz(event.end, userTimezone).format('HH:mm'),
      clientId: event.clientId || '',
      clientName: event.clientName || '',
      clientEmail: event.clientEmail || '',
      color: event.color || '#1B2B4B',
      addVideoConference: !!(event.hostLink && event.guestLink),
      hostLink: event.hostLink || '',
      guestLink: event.guestLink || '',
    });
    setSelectedEvent(event);
    setShowEventDetailsModal(true);
  };

  const handleSelectSlot = ({ start, end }) => {
    setFormData({
      title: '',
      description: '',
      location: '',
      date: moment.tz(start, userTimezone).format('YYYY-MM-DD'), // Ensure local date
      startTime: moment.tz(start, userTimezone).format('HH:mm'), // Ensure local time
      endTime: moment.tz(end, userTimezone).format('HH:mm'), // Ensure local time
      clientId: '',
      clientName: '',
      clientEmail: '',
      color: '#1B2B4B',
      addVideoConference: false, // Reset video conference toggle
      hostLink: '',
      guestLink: '',
    });
    setSelectedEvent(null);
    setShowEventDetailsModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'clientId') {
      const selectedClient = clients.find(client => client._id === value);
      setFormData({
        ...formData,
        clientId: value,
        clientName: selectedClient ? `${selectedClient.firstName} ${selectedClient.lastName}` : '',
        clientEmail: selectedClient?.email || '',
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  useEffect(() => {
    const storedEvents = localStorage.getItem('googleEvents');
    if (storedEvents) {
      try {
        const parsed = JSON.parse(storedEvents).map(event => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        })).filter(event => event.start instanceof Date && !isNaN(event.start) && event.end instanceof Date && !isNaN(event.end));
        setEvents(prevEvents => [...prevEvents, ...parsed]);
      } catch (e) {
        console.error('Failed to parse stored events:', e);
        localStorage.removeItem('googleEvents');
        fetchGoogleEvents();
      }
    } else {
      fetchGoogleEvents(); // Fetch from the server if not in localStorage
    }
  }, []); // Run once when the component mounts
 
  
  
  
  const handleSaveEvent = async (formData) => {
    try {
      console.log('Saving Event with FormData:', formData);
  
      const token = localStorage.getItem('token');
      const timezone = formData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  
      if (!formData.startTime || !formData.endTime) {
        console.error('Start time or end time is missing:', formData);
        showModal('error', 'Start time or end time is missing. Please provide valid values.');
        return;
      }
  
      const startDateTime = moment.tz(formData.startTime, timezone).utc().toISOString();
      const endDateTime = moment.tz(formData.endTime, timezone).utc().toISOString();
  
      console.log('Parsed Start DateTime:', startDateTime);
      console.log('Parsed End DateTime:', endDateTime);
  
      const eventData = {
        ...formData,
        startTime: startDateTime,
        endTime: endDateTime,
        timezone: userTimezone,
        // If event has circleVisible flag or belongs to a shared client, include circle fields
        ...(formData.circleVisible && circleId ? {
          circleVisible: true,
          circleId,
        } : {}),
      };
  
      let response;
      if (selectedEvent) {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/events/${selectedEvent.id}`, eventData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/events`, eventData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
  
      const savedEvent = {
        ...response.data,
        start: new Date(response.data.startTime),
        end: new Date(response.data.endTime),
      };
  
      if (selectedEvent) {
        setEvents((prevEvents) =>
          prevEvents.map((event) => (event.id === selectedEvent.id ? savedEvent : event))
        );
      } else {
        setEvents((prevEvents) => [...prevEvents, savedEvent]);
      }
  
      // Send the invite after creating or updating the event
      if (formData.clientEmail) {
        try {
          const invitePayload = {
            email: formData.clientEmail,
            eventDetails: {
              id: selectedEvent ? selectedEvent.id : response.data._id,
              title: formData.title,
              description: formData.description,
              location: formData.location,
              startTime: startDateTime,
              endTime: endDateTime,
              guestLink: formData.guestLink,
              timezone: userTimezone, // Pass the user's selected timezone
            },
          };
  
          console.log('Invite Payload:', invitePayload);
  
          const inviteResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/events/send-invite`,
            invitePayload,
            { headers: { Authorization: `Bearer ${token}` } }
          );
  
          if (inviteResponse.status === 200) {
            console.log('Invite sent successfully');
          } else {
            console.error('Failed to send invite');
          }
        } catch (inviteError) {
          console.error('Error sending invite:', inviteError);
        }
      }
  
      resetFormAndCloseModal();
      showModal('success', 'Event saved and invite sent successfully!');
    } catch (error) {
      console.error('Failed to create/update event:', error);
      showModal('error', 'An error occurred while saving the event. Please try again.');
    }
  };
  


  
const resetFormAndCloseModal = () => {
  setFormData({
    title: '',
    description: '',
    location: '',
    date: '',
    startTime: '',
    endTime: '',
    clientId: '',
    clientEmail: '',
    clientName: '',
    color: '#1B2B4B',
    hostLink: '',
    guestLink: '',
  });
  setSelectedEvent(null);
  setShowEventDetailsModal(false);
};



  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          console.warn('Token expired. Please reauthenticate.');
          handleLogout();
        }
      } catch (e) {
        console.error('Failed to decode token:', e);
      }
    }
  }, []); // Run once when the component mounts

    // Tooltip that shows the event title, description, location, and time
    const tooltipAccessor = (event) => {
      try {
        const parts = [];
        if (event.clientName) parts.push(event.clientName);
        parts.push(event.title || 'Untitled');
        if (event.description) parts.push(event.description);
        if (event.location) parts.push(`Location: ${event.location}`);
        if (event.start) parts.push(`Start: ${moment(event.start).format('h:mm A')}`);
        if (event.end) parts.push(`End: ${moment(event.end).format('h:mm A')}`);
        return parts.join('\n');
      } catch (e) {
        return event.title || 'Event';
      }
    };

    // Sanitize events - ensure all start/end are valid Date objects
    // This prevents Week/Day view crashes when dates are strings from localStorage
    const sanitizedEvents = events.filter(event => {
      const start = event.start instanceof Date ? event.start : new Date(event.start);
      const end = event.end instanceof Date ? event.end : new Date(event.end);
      return start instanceof Date && !isNaN(start.getTime()) &&
             end instanceof Date && !isNaN(end.getTime());
    }).map(event => ({
      ...event,
      start: event.start instanceof Date ? event.start : new Date(event.start),
      end: event.end instanceof Date ? event.end : new Date(event.end),
    })).filter((event, index, self) => {
      // Deduplicate: keep first occurrence of each title+start combination
      return index === self.findIndex(e =>
        e.title === event.title &&
        new Date(e.start).getTime() === new Date(event.start).getTime()
      );
    });
  

    if (isPro === null) {
      return <div className="calendar-container"><p>Loading...</p></div>;
    }

    if (isPro === false) {
      return <UpgradePrompt feature="Calendar & Scheduling" />;
    }

    return (
      <div className="calendar-container">
        {/* Back to Dashboard Button */}
        <button className="back-to-dashboard-btn" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        {circleId && (
          <button
            className="back-to-dashboard-btn"
            onClick={() => navigate(`/circles/${circleId}/calendar`)}
            style={{ marginLeft: '0.5rem', color: 'var(--accent)', borderColor: 'rgba(232,101,74,0.3)' }}
          >
            ✦ Circle Calendar
          </button>
        )}
    
        {/* Calendar Header */}
        {userData && (
          <div className="calendar-heading">
            <h1>Your Calendar</h1>
            <p className="calendar-tagline">Click any date to create an event. Click an event to edit or delete it.</p>
          </div>
        )}
    
        {/* Legend */}
        <div className="calendar-instructions">
          <div className="calendar-legend">
            <span className="legend-item">
              <span className="legend-dot" style={{ background: '#1B2B4B' }} />
              <span>Your events</span>
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: '#E8A030' }} />
              <span>Google Calendar</span>
            </span>
            <span className="legend-item">
              <span className="legend-dot" style={{ background: '#E8654A' }} />
              <span>Client bookings</span>
            </span>
          </div>
        </div>

    {/* Public Booking Link */}
    <div className="cal-share-bar">
      <span className="cal-share-label">Your booking link</span>
      <div className="cal-share-row">
        <input
          type="text"
          readOnly
          value={`https://app.airflyp.com/public-calendar/${userId || localStorage.getItem('userId')}`}
          className="cal-share-input"
          onClick={(e) => e.target.select()}
        />
        <button
          className="cal-share-copy-btn"
          onClick={() => {
            navigator.clipboard.writeText(
              `https://app.airflyp.com/public-calendar/${userId || localStorage.getItem('userId')}`
            );
            setPublicLinkCopied(true);
            setTimeout(() => setPublicLinkCopied(false), 2000);
          }}
        >
          {publicLinkCopied ? 'Copied ✓' : 'Copy link'}
        </button>
      </div>
    </div>

    {/* Timezone Selector */}
    <TimezoneSelector onTimezoneChange={handleTimezoneChange} />
    
        {/* Calendar Component */}
        <Calendar
  localizer={localizer}
  events={sanitizedEvents}
  startAccessor="start"
  endAccessor="end"
  views={['month', 'week', 'day', 'agenda']}
  tooltipAccessor={(event) => {
    try {
      const parts = [];
      if (event.clientName) parts.push(event.clientName);
      parts.push(event.title || 'Untitled');
      if (event.description) parts.push(event.description);
      if (event.location) parts.push(`Location: ${event.location}`);
      if (event.start) parts.push(`Start: ${moment(event.start).format('HH:mm')}`);
      if (event.end) parts.push(`End: ${moment(event.end).format('HH:mm')}`);
      return parts.join('\n');
    } catch (e) {
      return event.title || 'Event';
    }
  }}
  style={{ height: 600, minHeight: 400, margin: '20px 0' }}
  eventPropGetter={(event) => ({
    style: {
      backgroundColor: event.color || '#1B2B4B',
      color: '#ffffff',
      borderRadius: '6px',
      border: 'none',
      fontSize: '0.85rem',
      '--event-color': event.color || '#1B2B4B',
    },
  })}
  selectable
  onSelectSlot={handleSelectSlot}
  onSelectEvent={handleSelectEvent}
  formats={{
    timeGutterFormat: (date) => {
      try { return moment(date).format('h A'); } catch (e) { return ''; }
    },
    eventTimeRangeFormat: ({ start, end }) => {
      try {
        return `${moment(start).format('h:mm A')} – ${moment(end).format('h:mm A')}`;
      } catch (e) { return ''; }
    },
    agendaTimeRangeFormat: ({ start, end }) => {
      try {
        return `${moment(start).format('h:mm A')} – ${moment(end).format('h:mm A')}`;
      } catch (e) { return ''; }
    },
    dayHeaderFormat: (date) => {
      try { return moment(date).format('dddd, MMMM D'); } catch (e) { return ''; }
    },
  }}
/>

      {/* Event Details Modal */}
      {showEventDetailsModal && (
        <EventDetailsModal
          show={showEventDetailsModal}
          event={selectedEvent}
          formData={formData}
          handleChange={handleChange}
          onClose={resetFormAndCloseModal}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          clients={clients}
          circleId={circleId}
        />
      )}

      {/* Modal for Confirming Logout, Deleting Events, or Showing Error/Success Messages */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className={`modal ${modalType === 'success' ? 'modal-success' : modalType === 'error' ? 'modal-error' : 'modal-confirm'}`}>
            <div className="modal-header">
              {modalType === 'confirm' ? 'Confirm Action' : modalType === 'success' ? 'Success' : 'Error'}
            </div>
            <div className="modal-body">
              {modalMessage}
            </div>
            <div className="modal-footer">
              {modalType === 'confirm' ? (
                <>
                  <button onClick={handleConfirm} className="modal-close-btn">Confirm</button>
                  <button onClick={closeModal} className="modal-cancel-btn">Cancel</button>
                </>
              ) : (
                <button onClick={closeModal} className="modal-close-btn">OK</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyCalendar;